<template>
  <div v-bind:id="id" class="member-place-menu-item-additional-item-selector">
    <a class="item" v-on:click="isMenuOpen = !isMenuOpen">
      {{ $t('appMemberPlaceMenuItemAdditionalItemSelector.displaySetting') }}
      <icon-arrow-down
        class="icon-arrow"
        v-bind:class="{ reverse: isMenuOpen }"
      />
    </a>
    <div v-show="isMenuOpen">
      <b-form-radio-group
        v-bind:id="elementId.radio"
        stacked
        v-bind:checked="isSimple"
        class="radio-form"
        v-on:change="$_emitIsSimpleChangedEvent"
      >
        <label class="label">
          <b-form-radio class="radio" v-bind:value="true" />
          <span
            class="option-text"
            v-t.preserve="
              'appMemberPlaceMenuItemAdditionalItemSelector.option.simple'
            "
          />
        </label>
        <label class="label">
          <b-form-radio class="radio detail" v-bind:value="false" />
          <span
            class="option-text"
            v-t.preserve="
              'appMemberPlaceMenuItemAdditionalItemSelector.option.detailed'
            "
          />
          <a
            class="select-item-link"
            v-on:click="$_showModal"
            v-t.preserve="
              'appMemberPlaceMenuItemAdditionalItemSelector.message.selectDisplayItems'
            "
          />
        </label>
      </b-form-radio-group>
    </div>
    <!-- 項目選択モーダル -->
    <ifield-b-modal
      v-bind:id="elementId.modal"
      ref="modal"
      centered
      scrollable
      ok-variant="success"
      v-bind:title="
        $t(
          'appMemberPlaceMenuItemAdditionalItemSelector.title.selectDisplayItems'
        )
      "
      v-bind:ok-title="
        $t('appMemberPlaceMenuItemAdditionalItemSelector.action.save')
      "
      v-bind:cancel-title="
        $t('appMemberPlaceMenuItemAdditionalItemSelector.action.cancel')
      "
      body-class="selector-modal-body"
      v-on:ok="$_emitItemsChangedEvent"
      v-on:show="$emit('show-item-selector')"
      v-on:hide="$emit('hide-item-selector')"
    >
      <item-selector
        multi
        v-bind:titles="selectorTitles"
        v-bind:data="selectorOptions"
        v-bind:value-field="'item'"
        v-model="currentItems"
      />
    </ifield-b-modal>
  </div>
</template>

<script>
import IconArrowDown from '@/assets/ifield/icon_arrow_down.svg?component'
import { useHandleApiError } from '@/composables/UseHandleApiError'
import { usePlaceAdditionalItems } from '@/composables/place/UsePlaceAdditionalItems.js'
import { PLACE_ITEM_TYPE } from '@/js/util/PlaceLabelUtil.js'
import { RestApiClient } from 'ifield-core-components'
import StringUtil from '@/js/util/StringUtil.js'
import { mapGetters } from 'vuex'

/**
 * 地図画面のプレイスタブの「表示設定」
 *
 * 簡易表示と詳細表示の切り替えと、詳細表示で出す任意項目の選択を行う。IFIELD_V4-7150。
 * ユーザーリストの `AppMemberUserMenuItemAdditionalItemSelector.vue` と同じ作りにしてある
 */
export default {
  name: 'AppMemberPlaceMenuItemAdditionalItemSelector',
  components: {
    IconArrowDown,
  },
  props: {
    /**
     * ルート要素のID
     * 子要素のIDのprefixとしても使用
     */
    id: {
      type: String,
      default: 'place-menu-item-additional-item-selector',
    },
    /**
     * 項目値
     */
    items: {
      type: Array,
      default: () => [],
    },
    /**
     * 簡易表示フラグ
     */
    isSimple: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const { handleApiError } = useHandleApiError()
    const { selectableItems } = usePlaceAdditionalItems()
    return { handleApiError, selectableItems }
  },
  data: function () {
    return {
      // 選択している項目
      currentItems: [],
      // 表示切り替えメニューの開閉フラグ
      isMenuOpen: false,
      // プレイス属性（IDと名称の対応）
      placeAttributeMap: new Map(),
    }
  },
  computed: {
    ...mapGetters('sensorItem', ['sensorItemMap']),
    /**
     * 要素ID
     * @type {Object}
     */
    elementId: function () {
      return {
        radio: `${this.id}-radio`,
        modal: `${this.id}-modal`,
      }
    },
    /**
     * ItemSelector用のタイトル定義
     * @type {Array.<Object>}
     */
    selectorTitles: function () {
      return [
        {
          key: 'name',
          label: this.$t(
            'appMemberPlaceMenuItemAdditionalItemSelector.itemType'
          ),
        },
      ]
    },
    /**
     * ItemSelector用の選択肢データ
     * @type {Array.<Object>}
     */
    selectorOptions: function () {
      return this.selectableItems.map((item) => {
        const [type, targetId] = item.split(':')
        return {
          item,
          name: this.$_getOptionLabel(type, targetId),
        }
      })
    },
  },
  watch: {
    items: {
      immediate: true,
      handler: function (value) {
        this.currentItems = [...value]
      },
    },
  },
  created: function () {
    this.$_getSensorItems()
    this.$_getPlaceAttributes()
  },
  methods: {
    /**
     * センサー項目データ取得
     */
    $_getSensorItems: async function () {
      try {
        await this.$store.dispatch('sensorItem/updateSensorItemsWithThrottle')
      } catch (error) {
        this.handleApiError(error, () =>
          this.$log.warn('failed to fetch sensorItems. use fetched before.')
        )
      }
    },
    /**
     * プレイス属性データ取得
     */
    $_getPlaceAttributes: async function () {
      const url = RestApiClient.createApiUrl('place_attribute')
      try {
        const json = await RestApiClient.executeGetApi(url)
        this.placeAttributeMap = new Map(
          (json.data || []).map((attribute) => [
            +attribute.id,
            attribute.attribute_name,
          ])
        )
      } catch (error) {
        this.handleApiError(error, () =>
          this.$log.warn('failed to fetch placeAttributes.')
        )
      }
    },
    /**
     * 選択肢の名称を返す
     * @param {String} itemType 項目種別
     * @param {String} targetId 対象ID（センサー項目ID・プレイス属性ID）
     * @returns {String} 選択肢の名称
     */
    $_getOptionLabel: function (itemType, targetId) {
      if (itemType === PLACE_ITEM_TYPE.SENSOR_DATA) {
        // センサー測定項目名称に変換
        return this.sensorItemMap.get(+targetId)?.sensor_item_name || ''
      } else if (itemType === PLACE_ITEM_TYPE.ATTRIBUTE) {
        // プレイス属性名称に変換
        return this.placeAttributeMap.get(+targetId) || ''
      } else if (Object.values(PLACE_ITEM_TYPE).includes(itemType)) {
        return this.$t(
          `appMemberPlaceMenuItemAdditionalItemSelector.item.${StringUtil.snakeToCamel(
            itemType
          )}`
        )
      } else {
        return ''
      }
    },
    /**
     * 項目選択モーダル表示
     */
    $_showModal: function () {
      this.$refs['modal'].show()
    },
    /**
     * 簡易表示フラグ変更イベントを発火する
     * @param {Boolean} value 簡易表示するならtrue
     */
    $_emitIsSimpleChangedEvent: function (value) {
      /**
       * 簡易表示フラグ変更イベント
       * @param {Boolean} value 簡易表示するならtrue
       */
      this.$emit('is-simple-changed', value)
    },
    /**
     * 表示項目変更イベントを発火する
     */
    $_emitItemsChangedEvent: function () {
      /**
       * 表示項目変更イベント
       * @param {Array.<String>} items 選択した表示項目
       */
      this.$emit('items-changed', [...this.currentItems])
    },
  },
}
</script>

<style lang="scss" scoped>
.radio-form {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  row-gap: 2px;
  padding: 0 16px 10px 16px;

  .label {
    font-size: $font-size-middle;
    white-space: nowrap;
    display: inline-flex;
    justify-content: flex-start;
    align-items: center;
    margin: 0;

    &:hover {
      cursor: pointer;
    }

    ::v-deep .custom-control {
      // テキストやフォームをラジオボタンに少し近づけるため右側 margin を調整
      margin: 0 -2px 0 0;
    }

    ::v-deep .custom-control-input {
      // 選択時のスタイル => 色を緑系にする
      &:checked ~ .custom-control-label::before {
        border-color: $entity-color-green;
        background-color: $entity-color-green;
      }

      // focus時のshadowは出さない
      &:focus ~ .custom-control-label::before {
        box-shadow: none;
      }

      // afterが一番上に重なるのでafterにcursor指定する
      &:hover ~ .custom-control-label::after {
        cursor: pointer;
      }
    }

    .select-item-link {
      color: $text-color-link;
      margin-left: 8px;
    }
  }
}

::v-deep .selector-modal-body {
  background-color: #ffffff;
}

.item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 30px;
  padding: 8px 16px;
  color: $text-color;
  font-size: $font-size-middle;
  line-height: 1;
  text-decoration: none;
  background-color: #ffffff;
  transition: 0.2s;

  &:hover {
    background-color: #f6f6f6;
    cursor: pointer;
  }
}
.icon-arrow {
  fill: none;
  stroke: $text-color-weak;
  stroke-width: 1.5px;
  margin: -8px -4px;
  transition: transform 0.3s;
  &.reverse {
    transform: rotate(180deg);
  }
}
</style>
